import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import weekday from 'dayjs/plugin/weekday';
import isToday from 'dayjs/plugin/isToday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isBetween from 'dayjs/plugin/isBetween';
import DayOfYear from 'dayjs/plugin/dayOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import advancedFormat from 'dayjs/plugin/advancedFormat';
// Import the necessary plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekday);
dayjs.extend(isToday);
dayjs.extend(weekOfYear);
dayjs.extend(isBetween);
dayjs.extend(DayOfYear);
dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);

// Default timezone if none is provided or available
export const DEFAULT_TIMEZONE = 'UTC';

export type TZDayjsFunction = (
  date?: Parameters<typeof import('dayjs')>[0] | null,
  unix?: boolean,
  tz?: string | null | undefined
) => Dayjs;

/**
 * Creates a Day.js object, applying the specified timezone.
 * Defaults to UTC if no timezone is provided.
 *
 * @param date - The date input (compatible with dayjs constructor).
 * @param unix - Whether the input 'date' is a Unix timestamp (in seconds). Defaults to false.
 * @param tz - The IANA timezone string (e.g., 'America/Denver', 'Europe/London'). Defaults to UTC.
 * @returns A Dayjs object configured with the specified timezone.
 */
export const TZDayjs = (
  date?: Parameters<typeof dayjs>[0],
  unix = false,
  tz: string | null | undefined = DEFAULT_TIMEZONE
): Dayjs => {
  const targetTimezone = tz || DEFAULT_TIMEZONE;
  // Handle unix timestamps
  if (unix) {
    return dayjs.unix(Number(date)).tz(targetTimezone);
  }

  // If no date provided, use current time
  if (!date) {
    return dayjs().tz(targetTimezone, true);
  }

  // For string dates, we need to handle different formats
  if (typeof date === 'string') {
    // Check if it's an ISO string with 'Z' (UTC)
    const isUTC = date.endsWith('Z');

    if (isUTC) {
      // For UTC dates, parse in UTC and then convert to target timezone
      return dayjs.utc(date).tz(targetTimezone);
    }

    if (date.length <= 10) {
      // For dates with offset or no timezone info, parse directly in target timezone
      return dayjs.tz(date, targetTimezone);
    }

    // For dates with offset or no timezone info, parse directly in target timezone
    return dayjs(date).tz(targetTimezone, false);
  }

  // For other date types (Date objects, etc.), parse in target timezone
  return dayjs.tz(date, targetTimezone);
};
